<template>
  <div
    class="login-container min-h-screen w-full flex items-center justify-center p-4 sm:p-6"
    :style="{ '--login-bg': `url(${loginBg})` }"
  >
    <!-- 데스크톱 배경 이미지 레이어 (모바일 숨김) -->
    <div class="login-bg-layer hidden md:block"></div>

    <!-- 데스크톱 배경 오버레이 및 블러 효과 (모바일 숨김) -->
    <div class="login-overlay hidden md:block"></div>

    <!-- 메인 컨텐츠 영역 (슬로건 문구 + 사각 로그인 카드) -->
    <div
      class="relative z-10 w-full max-w-6xl flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14"
    >
      <!-- 좌측 메인배경 슬로건 (데스크톱에서 표시, 모바일/폰에서는 숨김) -->
      <div
        class="hidden lg:flex flex-col flex-1 max-w-2xl xl:max-w-3xl text-white select-none"
      >
        <div
          class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-sm font-semibold tracking-wider text-white mb-6 w-fit shadow-sm"
        >
          <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
          SAMWOO Sales Management Support System
        </div>

        <h1
          class="text-4xl xl:text-4xl font-extrabold leading-tight xl:leading-[1.2] tracking-tight drop-shadow-lg text-white"
        >
          가장 신뢰할 수 있는 철강의 이름으로, <br/><span class="text-yellow-300">삼우강업㈜</span>이 곁에서 함께합니다.

        </h1>
        <p
          class="mt-6 text-lg xl:text-xl text-white leading-relaxed font-light"
        >
          산업의 내일을 더 단단하게 세우며, 변함없는 품질과 가치를 현장에 전합니다.
          <!-- <br class="hidden xl:inline" /> -->
        </p>

        <!-- 핵심 가치 태그 -->
        <div
          class="mt-8 flex items-center gap-6 pt-6 border-t border-white/20 text-sm text-white"
        >
          <div class="flex items-center gap-2">
            <i class="fas fa-shield-alt text-white"></i>
            <span>신뢰</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fas fa-cogs text-white"></i>
            <span>품질</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fas fa-handshake text-white"></i>
            <span>동행</span>
          </div>
        </div>
      </div>

      <!-- 우측 사각형 로그인 카드 (모바일에서는 단독 중앙 배치) -->
      <div class="w-full lg:w-auto flex-shrink-0 flex justify-center lg:justify-end">
        <transition
          enter-active-class="transform transition duration-500 ease-out"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
        >
          <div
            v-if="showLogin"
            class="login-card w-full max-w-[420px] bg-white rounded-2xl shadow-xl md:shadow-2xl border border-gray-100 p-8 sm:p-10"
          >
        <!-- 상단 헤더 / 로고 -->
        <div class="text-center mb-8">
          <div
            class="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
          >
            <img :src="logoImg" alt="Logo" class="w-10 h-10 object-contain" />
          </div>
          <h2 class="text-2xl font-bold text-gray-800 tracking-tight">
            로그인
          </h2>
          <p class="text-xs text-gray-400 mt-1.5 font-medium">
            삼우강업㈜ 영업관리지원시스템
          </p>
        </div>

        <!-- 폼 필드 -->
        <div class="space-y-4">
          <!-- 아이디 -->
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1.5">
              아이디
            </label>
            <UCText
              ref="refTxtId"
              v-model="txtId"
              placeholder="아이디를 입력해주세요"
              :rules="[required('아이디를 입력해주세요')]"
              :required-asterisk="true"
              validate-on="submit"
            />
          </div>

          <!-- 비밀번호 -->
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1.5">
              비밀번호
            </label>
            <UCText
              ref="refTxtPassword"
              v-model="txtPassword"
              type="password"
              placeholder="비밀번호를 입력해주세요"
              :rules="[required('비밀번호를 입력해주세요')]"
              :required-asterisk="true"
              validate-on="submit"
              @keyup.enter="txtPasswordEnter"
            />
          </div>

          <!-- 옵션: 아이디 저장 -->
          <div class="flex items-center justify-between pt-1">
            <label class="flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                id="remember"
                v-model="chkRememberMe"
                class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
              />
              <span class="ml-2 text-sm text-gray-600 hover:text-gray-900">
                아이디 저장
              </span>
            </label>
          </div>

          <!-- 에러 메시지 알림 -->
          <div
            v-if="errorMessages.length > 0"
            class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs flex items-center gap-2"
          >
            <i
              class="fas fa-exclamation-circle text-sm text-red-500 flex-shrink-0"
            ></i>
            <span>{{ errorMessages[0] }}</span>
          </div>

          <!-- 로그인 버튼 -->
          <div class="pt-2">
            <button
              type="button"
              class="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-base cursor-pointer"
              :disabled="isLoading"
              @click="btnLoginClick"
            >
              <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
              <span>{{ isLoading ? "로그인 중..." : "로그인" }}</span>
            </button>
          </div>
        </div>

        <!-- 하단 카피라이트 -->
        <div class="mt-8 pt-5 border-t border-gray-100 text-center">
          <p class="text-[11px] text-gray-400">
            © 삼우강업㈜ SMSS. All rights reserved.
          </p>
        </div>
      </div>
    </transition>
  </div>
</div>
</div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/store/userStore";
import { required } from "@/utils/rule";
import UCText from "@/components/UserControl/UCText.vue";

import loginBg from "@/assets/img/loginbg.jpg";
import logoImg from "@/assets/img/logo.png";

// router, store 인스턴스
const router = useRouter();
const userStore = useUserStore();

// 상태 변수
const showLogin = ref(false);
const isLoading = ref(false);

const txtId = ref("");
const refTxtId = ref(null);

const txtPassword = ref("");
const refTxtPassword = ref(null);

const errorMessages = ref([]);
const chkRememberMe = ref(false);

onMounted(() => {
  // 컴포넌트가 로드되면 애니메이션 시작
  setTimeout(() => {
    showLogin.value = true;
  }, 50);

  const savedId = localStorage.getItem("rememberedId");
  if (savedId) {
    txtId.value = savedId;
    chkRememberMe.value = true;
  }
});

// 비밀번호 입력 후 엔터 처리
function txtPasswordEnter() {
  btnLoginClick();
}

// 로그인 버튼 클릭
async function btnLoginClick() {
  errorMessages.value = [];
  const v1 = refTxtId.value.validate();
  const v2 = refTxtPassword.value.validate();
  if (!v1 || !v2) return;

  isLoading.value = true;
  try {
    const res = await userStore.loginByPassword({
      userId: txtId.value,
      userPassword: txtPassword.value,
    });

    if (res.Code === 200) {
      if (chkRememberMe.value) {
        localStorage.setItem("rememberedId", txtId.value);
      } else {
        localStorage.removeItem("rememberedId");
      }
      router.push("/scm/home");
    } else {
      errorMessages.value[0] =
        res.Message || "아이디 또는 비밀번호를 다시 확인하세요!";
    }
  } catch (err) {
    console.error("login fail", err);
    errorMessages.value[0] = "아이디 또는 비밀번호를 다시 확인하세요!";
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.login-container {
  position: relative;
  background-color: #f1f5f9;
  overflow: hidden;
}

/* 폰(모바일)인 경우: 배경 이미지 레이어가 hidden 처리되어 단색 배경과 사각 로그인만 표시 */
@media (max-width: 767px) {
  .login-container {
    background-color: #f8fafc;
  }
}

/* 태블릿 / 데스크탑 (768px 이상): 사옥 배경 레이어 */
.login-bg-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: var(--login-bg);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}

/* 데스크탑 블러 & 틴트 오버레이 (확실하고 세련된 10px 블러 효과) */
.login-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1;
}

.login-card {
  position: relative;
  z-index: 10;
}

/* UCText 인풋 스타일 정돈 */
:deep(.relative > input) {
  height: 44px;
  padding-left: 14px;
  padding-right: 36px;
  border-radius: 8px;
  font-size: 0.925rem;
}
</style>
